import {FC, useEffect, useState} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Button} from '../../../components/Button/Button'
import {Input} from '../../../components/Input/Input'
import Swal from 'sweetalert2'
import {GET, POST, PUT} from '../../../services/api'
import {Loading} from '../../../components/Loading/Loading'
import {useNavigate, useParams} from 'react-router-dom'
import {i18n} from '../../../translate/i18n'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'
import uuid from 'react-uuid'
import {ActivityCategory, AgeGroup, getCategoryLabel, getAgeGroupLabel, FormDataType} from './types'
import {EditorQuill} from '../../../components/EditorQuill/EditorQuill'
import {TitleFreePriceInput} from '../Comuns'

const EditHotelActivityContent: FC = () => {
  const navigate = useNavigate()
  const {id} = useParams()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState<FormDataType>({
    title: '',
    description: '',
    period: '',
    time: '',
    location: '',
    capacity: '',
    category: ActivityCategory.ENTERTAINMENT,
    ageGroup: AgeGroup.FAMILY,
    price: '',
    titleFreePrice: '',
    requiresReservation: false,
    isActive: true,
  })
  const [image, setImage] = useState<File | string | null>(null)
  const [firstStateImage, setFirstStateImage] = useState<File | string | null>(null)

  const handleInputChange = (
    field: string,
    value: string | ActivityCategory | AgeGroup | boolean
  ) => {
    setFormData((prev) => {
      const updated = {
        ...prev,
        [field]: value,
      } as any

      // Se o campo alterado for 'price', limpar titleFreePrice quando preço for maior que 0
      if (field === 'price') {
        const num = Number(String(value))
        if (Number.isFinite(num) && num > 0) {
          updated.titleFreePrice = ''
        }
      }

      return updated
    })
  }

  const validateForm = () => {
    if (!formData.title.trim()) {
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('hospitality_activities.msg_error_update')),
        text: String(i18n.t('hospitality_activities.msg_error_required_title')),
      })
      return false
    }

    if (formData.title.length > 255) {
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('hospitality_activities.msg_error_update')),
        text: String(i18n.t('hospitality_activities.msg_error_title_length')),
      })
      return false
    }

    if (!formData.description.trim()) {
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('hospitality_activities.msg_error_update')),
        text: String(i18n.t('hospitality_activities.msg_error_required_description')),
      })
      return false
    }

    if (!formData.time.trim()) {
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('hospitality_activities.msg_error_update')),
        text: String(i18n.t('hospitality_activities.msg_error_required_time')),
      })
      return false
    }

    if (formData.time.length > 100) {
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('hospitality_activities.msg_error_update')),
        text: String(i18n.t('hospitality_activities.msg_error_time_length')),
      })
      return false
    }

    if (!formData.location.trim()) {
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('hospitality_activities.msg_error_update')),
        text: String(i18n.t('hospitality_activities.msg_error_required_location')),
      })
      return false
    }

    if (formData.location.length > 255) {
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('hospitality_activities.msg_error_update')),
        text: String(i18n.t('hospitality_activities.msg_error_location_length')),
      })
      return false
    }

    if (!formData.capacity.trim()) {
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('hospitality_activities.msg_error_update')),
        text: String(i18n.t('hospitality_activities.msg_error_required_capacity')),
      })
      return false
    }

    if (formData.capacity.length > 50) {
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('hospitality_activities.msg_error_update')),
        text: String(i18n.t('hospitality_activities.msg_error_capacity_length')),
      })
      return false
    }

    if (formData.price && parseFloat(formData.price) < 0) {
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('hospitality_activities.msg_error_update')),
        text: String(i18n.t('hospitality_activities.msg_error_price_negative')),
      })
      return false
    }

    return true
  }

  const getActivity = async () => {
    try {
      setLoading(true)
      const response = await GET(`/hotel-activities/${id}`)

      if (response.success) {
        const activity = response.data[0] || response.data
        setFormData({
          title: activity.title || '',
          description: activity.description || '',
          period: activity.period || '',
          time: activity.time || '',
          location: activity.location || '',
          capacity: activity.capacity || '',
          category: activity.category || ActivityCategory.ENTERTAINMENT,
          ageGroup: activity.ageGroup || AgeGroup.FAMILY,
          price: activity.price ? activity.price.toString() : '',
          titleFreePrice: activity.titleFreePrice || '',
          requiresReservation: activity.requiresReservation || false,
          isActive: activity.isActive !== undefined ? activity.isActive : true,
        })

        // Configurar imagem
        const imageUrl = activity.image
          ? activity.image.substring(0, 4) === 'http'
            ? activity.image
            : process.env.REACT_APP_AWS_S3_URL + activity.image
          : null
        setImage(imageUrl)
        setFirstStateImage(imageUrl)
      } else {
        throw new Error(response.message || 'Erro ao carregar atividade')
      }

      setLoading(false)
    } catch (e: any) {
      setLoading(false)
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('hospitality_activities.msg_error_load')),
        text: String(e.message || i18n.t('hospitality_activities.msg_error_load')),
      }).then(() => {
        navigate('/hospitality/hotel-activities')
      })
      console.log(e)
    }
  }

  const updateActivity = async () => {
    if (!validateForm()) return

    try {
      setLoading(true)

      let imageRes = {
        success: false,
        image: '',
      }

      let response
      if (image && image !== firstStateImage) {
        // Nova imagem foi selecionada
        if (firstStateImage && typeof firstStateImage === 'string') {
          // Remove a imagem anterior se existir
          await POST('/upload-images/delete-one', {
            url: firstStateImage,
          })
        }

        if (typeof image === 'object') {
          // Upload da nova imagem
          const formDataImg = new FormData()
          formDataImg.append('file', image)
          formDataImg.append('nameImage', `activity-${uuid()}.jpg`)
          formDataImg.append('originalSize', 'true')
          imageRes = await POST('/upload-images/upload-one', formDataImg)
        }

        const payload = {
          title: formData.title.trim(),
          description: formData.description.trim(),
          time: formData.time.trim(),
          location: formData.location.trim(),
          period: formData.period,
          capacity: formData.capacity.trim(),
          image: imageRes.success ? imageRes.image : 'default/no-image.png',
          category: formData.category,
          ageGroup: formData.ageGroup,
          price: formData.price ? parseFloat(formData.price) : 0,
          titleFreePrice: formData.titleFreePrice || null,
          requiresReservation: formData.requiresReservation,
          isActive: formData.isActive,
        }

        response = await PUT(`/hotel-activities/${id}`, payload)
      } else {
        // Sem mudança na imagem ou imagem removida
        const payload = {
          title: formData.title.trim(),
          description: formData.description.trim(),
          time: formData.time.trim(),
          location: formData.location.trim(),
          capacity: formData.capacity.trim(),
          period: formData.period,
          image: !image ? 'default/no-image.png' : undefined,
          category: formData.category,
          ageGroup: formData.ageGroup,
          price: formData.price ? parseFloat(formData.price) : 0,
          titleFreePrice: formData.titleFreePrice || null,
          requiresReservation: formData.requiresReservation,
          isActive: formData.isActive,
        }

        // Remove o campo image se não houve mudança
        if (image === firstStateImage) {
          delete payload.image
        }

        response = await PUT(`/hotel-activities/${id}`, payload)
      }

      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: String(i18n.t('hospitality_activities.btn_edit')),
          text: String(i18n.t('hospitality_activities.msg_success_update')),
        }).then(() => {
          navigate('/hospitality/hotel-activities')
        })
      } else {
        throw new Error(response.message || i18n.t('hospitality_activities.msg_error_update'))
      }

      setLoading(false)
    } catch (e: any) {
      setLoading(false)
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('hospitality_activities.msg_error_update')),
        text: String(e.message || i18n.t('hospitality_activities.msg_error_update')),
      })
      console.log(e)
    }
  }

  useEffect(() => {
    if (id) {
      getActivity()
    }
  }, [id])

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>
                  {i18n.t('hospitality_activities.edit_title')}
                </span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  {i18n.t('hospitality_activities.edit_subtitle')}
                </span>
              </h3>
              <div className='card-toolbar'>
                <Button
                  text={i18n.t(`buttons.back`)}
                  color='info'
                  iconColor='#FFF'
                  icon='fas fa-arrow-left'
                  size='small'
                  horizontalPadding={6}
                  btnAttr={{
                    type: 'button',
                  }}
                  click={() => navigate('/hospitality/hotel-activities')}
                  btnClass='mb-5 fw-bolder me-3'
                />
              </div>
            </div>
            <div className='card-body'>
              <form>
                <div className='row'>
                  <div className='col-md-6'>
                    <Input
                      inputAttr={{
                        type: 'default',
                      }}
                      label={i18n.t('hospitality_activities.field_title')}
                      placeholder={i18n.t('hospitality_activities.field_title_placeholder')}
                      maxCaracteres={255}
                      value={formData.title}
                      change={(value: string) => handleInputChange('title', value)}
                      smalltext={i18n.t('hospitality_activities.field_title_hint')}
                    />
                  </div>
                  <div className='col-md-6'>
                    <Input
                      inputAttr={{
                        type: 'default',
                      }}
                      label={i18n.t('hospitality_activities.field_time')}
                      placeholder={i18n.t('hospitality_activities.field_time_placeholder')}
                      maxCaracteres={100}
                      value={formData.time}
                      change={(value: string) => handleInputChange('time', value)}
                      smalltext={i18n.t('hospitality_activities.field_time_hint')}
                    />
                  </div>
                </div>

                <div className='row'>
                  <div className='col-md-10'>
                    <EditorQuill
                      value={formData.description}
                      label={i18n.t('hospitality_activities.field_description')}
                      placeholder={i18n.t('hospitality_activities.field_description_placeholder')}
                      change={(value: string) => handleInputChange('description', value)}
                      maxLength={5000} // Limite de 5000 caracteres
                    />
                  </div>
                  <div className='col-md-2'>
                    <Input
                      inputAttr={{
                        type: 'image',
                      }}
                      label={i18n.t('hospitality_activities.field_image')}
                      value={image}
                      change={(value: React.BaseSyntheticEvent) =>
                        setImage(value ? value.target.files[0] : null)
                      }
                    />
                  </div>
                </div>

                <div className='row'>
                  <div className='col-md-12'>
                    <Input
                      inputAttr={{
                        type: 'default',
                      }}
                      label={i18n.t('hospitality_activities.field_location')}
                      placeholder={i18n.t('hospitality_activities.field_location_placeholder')}
                      maxCaracteres={255}
                      value={formData.location}
                      change={(value: string) => handleInputChange('location', value)}
                      smalltext={i18n.t('hospitality_activities.field_location_hint')}
                    />
                  </div>
                  <div className='col-md-6'>
                    <Input
                      inputAttr={{
                        type: 'hour',
                      }}
                      label={i18n.t('hospitality_activities.field_period')}
                      placeholder={i18n.t('hospitality_activities.field_period_placeholder')}
                      maxCaracteres={50}
                      value={formData.period}
                      change={(value: string) => handleInputChange('period', value)}
                    />
                  </div>
                  <div className='col-md-6'>
                    <Input
                      inputAttr={{
                        type: 'number',
                      }}
                      label={i18n.t('hospitality_activities.field_capacity')}
                      placeholder={i18n.t('hospitality_activities.field_capacity_placeholder')}
                      maxCaracteres={50}
                      value={formData.capacity}
                      change={(value: string) => handleInputChange('capacity', value)}
                      smalltext={i18n.t('hospitality_activities.field_capacity_hint')}
                    />
                  </div>
                </div>

                <div className='row'>
                  <div className='col-md-6'>
                    <Input
                      inputAttr={{
                        type: 'select',
                      }}
                      label={i18n.t('hospitality_activities.field_category')}
                      options={Object.values(ActivityCategory).map((category) => ({
                        value: category,
                        select: getCategoryLabel(category),
                      }))}
                      value={formData.category}
                      change={(value: ActivityCategory) => handleInputChange('category', value)}
                    />
                  </div>
                  <div className='col-md-6'>
                    <Input
                      inputAttr={{
                        type: 'select',
                      }}
                      label={i18n.t('hospitality_activities.field_age_group')}
                      options={Object.values(AgeGroup).map((ageGroup) => ({
                        value: ageGroup,
                        select: getAgeGroupLabel(ageGroup),
                      }))}
                      value={formData.ageGroup}
                      change={(value: AgeGroup) => handleInputChange('ageGroup', value)}
                    />
                  </div>
                </div>

                <div className='row'>
                  <div className='col-md-6'>
                    <TitleFreePriceInput
                      value={formData.titleFreePrice || ''}
                      onChange={(value) => handleInputChange('titleFreePrice', value || '')}
                      label='Título Para Valor Sem Custo'
                      placeholder='Ex:. Cortesia do Hotel'
                      maxCaracteres={30}
                      disabled={parseFloat(formData.price) !== 0 && formData.price !== ''}
                      toolTip={true}
                      toolTiptext='Se o preço estiver zerado, este texto poderá ser exibido no WebApp. Se vazio, nada será mostrado.'
                      toolTipIcon='info-circle'
                    />
                  </div>
                  <div className='col-md-6'>
                    <Input
                      inputAttr={{
                        type: 'number',
                      }}
                      label={i18n.t('hospitality_activities.field_price')}
                      placeholder={i18n.t('hospitality_activities.field_price_placeholder')}
                      value={formData.price}
                      change={(value: string) => handleInputChange('price', value)}
                      smalltext={i18n.t('hospitality_activities.field_price_hint')}
                      minNumber={0}
                    />
                  </div>
                </div>

                <div className='row'>
                  <div className='col-md-6'>
                    <Input
                      inputAttr={{
                        type: 'checkbox',
                      }}
                      label={i18n.t('hospitality_activities.field_requires_reservation')}
                      checkSwift={true}
                      checked={formData.requiresReservation}
                      change={(checked: boolean) =>
                        handleInputChange('requiresReservation', checked)
                      }
                      smalltext={i18n.t('hospitality_activities.field_requires_reservation_hint')}
                    />
                  </div>
                  <div className='col-md-6'>
                    <Input
                      inputAttr={{
                        type: 'checkbox',
                      }}
                      label={i18n.t('hospitality_activities.field_is_active')}
                      checkSwift={true}
                      checked={formData.isActive}
                      change={(checked: boolean) => handleInputChange('isActive', checked)}
                      smalltext={i18n.t('hospitality_activities.field_is_active_hint')}
                    />
                  </div>
                </div>

                <div className='d-flex justify-content-end'>
                  <Button
                    text={i18n.t(`buttons.cancel`)}
                    color='danger'
                    size='small'
                    horizontalPadding={8}
                    btnAttr={{
                      type: 'button',
                    }}
                    click={() => navigate('/hospitality/hotel-activities')}
                    btnClass='me-3'
                  />
                  <Button
                    text={i18n.t(`general.edit`)}
                    color='primary'
                    iconColor='#FFF'
                    icon='fas fa-save'
                    size='small'
                    horizontalPadding={8}
                    btnAttr={{
                      type: 'button',
                    }}
                    click={updateActivity}
                  />
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const EditHotelActivity: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Dashboard', path: 'dashboard', isActive: false},
          {title: 'Atividades do Hotel', path: 'hotel-activities', isActive: false},
          {title: 'Editar Atividade', path: 'hotel-activities/editar', isActive: true},
        ]}
      >
        Editar Atividade do Hotel
      </PageTitle>
      <EditHotelActivityContent />
    </>
  )
}

export {EditHotelActivity}
